import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { MAX_LIMIT_FOR_FETCHING_ITEMS, USER_ROLE } from '~/shared/constants';
import findManyLightCourseCategoryQuery from '~/shared/graphql-queries/course-categories/find-many-light-course-category-query';
import {
  createOneEventMutation,
  removeByIdEventMutation,
  updateByIdEventMutation,
} from '~/shared/graphql-queries/events';
import findByIdEventQuery from '~/shared/graphql-queries/events/find-by-id-event-query';
import paginationCalendarLightEventQuery from '~/shared/graphql-queries/events/pagination-calendar-light-event-query';
import removeByIdsEventMutation from '~/shared/graphql-queries/events/remove-by-ids-event-mutation';
import updateEventDateTimeMutation from '~/shared/graphql-queries/events/update-event-date-time-mutation';
import userGroupFragment from '~/shared/graphql-queries/user-groups/user-group-fragment';
import { userFragment } from '~/shared/graphql-queries/users';
import { itemsPagination } from '~/shared/helpers/apollo-helpers';
import { initializeApollo } from '~/shared/libs/apollo/apollo-provider';
import { setCourseCategories } from '~/shared/store/slices/course-categories/course-categories-slice';

type UpdateEventDateTimeArgsType = {
  _id: string;
  dateTime: string[];
};
type FindByIdEventQueryResultType = {
  findByIdEventQuery?: EventType | null;
};

type FetchEventDetailsByIdArgsType = {
  _id: string;
};
type CourseCategoryType = {
  _id: string;
  title?: string | null;
  alias?: string | null;
  categoryIndex?: number | null;
  data?: {
    course?: string | null;
    isCommonCourseCategory?: boolean | null;
    color?: string | null;
  } | null;
};

type UserType = {
  _id: string;
  firstName?: string | null;
  lastName?: string | null;
  role?: string | null;
};

type UserGroupType = {
  _id: string;
  title?: string | null;
};

type EventDataType = {
  course?: string | null;
  courseCategory?: string | null;
  lesson?: string | null;
  studentsGroup?: string | null;
  users?:
    | string[]
    | Array<{
        _id: string;
        firstName?: string | null;
        lastName?: string | null;
        email?: string | null;
      }>
    | null;
  eventType?: string | null;
};

type EventType = {
  _id: string;
  title?: string | null;
  color?: string | null;
  dateTime?: string[] | null;
  status?: string | null;
  data?: EventDataType | null;
};

type CalendarStateType = {
  data?: Record<string, unknown>;
  currIndex: number;
  isLessonEventCreationMode: boolean;
  courseCategories: CourseCategoryType[];
  courseCategoryStudentGroups: UserGroupType[];
  courseCategoryStudents: UserType[];
  courseId: string | null;
  eventDetailsById: Record<string, EventType>;
  eventDetailsLoadingById: Record<string, boolean>;
  selectedCourseCategory: string | null;
  selectedCourseCategoryId: string | null;
  selectedCourseCategoryIds: string[];
  events: EventType[];
  isLoading: boolean;
  error: string | null;
  contents: CourseCategoryType[];
};

type RootStateLike = {
  users?: {
    userWithCourses?: {
      _id?: string | null;
      role?: string | null;
    } | null;
  } | null;
  calendar?: {
    courseId?: string | null;
    selectedCourseCategoryIds?: string[];
    eventDetailsById?: Record<string, EventType>;
  } | null;
};

type FetchCourseCategoriesArgsType = {
  courseId?: string | null;
};

type FetchEventsArgsType =
  | {
      courseId?: string | null;
      userId?: string | null;
      // –æ—Å—Ç–∞–≤–ª—è–µ–º, —á—Ç–æ–±—ã —Å—Ç–∞—Ä—ã–µ –≤—ã–∑–æ–≤—ã –Ω–µ –ø–∞–¥–∞–ª–∏,
      // –Ω–æ –≤ fetchEvents –¥–ª—è —Ñ–∏–ª—å—Ç—Ä–∞ –ù–ï –∏—Å–ø–æ–ª—å–∑—É–µ–º
      selectedCourseCategoryId?: string | null;
      // —Ç–æ–ª—å–∫–æ —ç—Ç–æ –ø–æ–ª–µ –¥–æ–ª–∂–Ω–æ —Ñ–∏–ª—å—Ç—Ä–æ–≤–∞—Ç—å –∫–∞–ª–µ–Ω–¥–∞—Ä—å –ø–æ –º–æ–¥—É–ª—è–º
      selectedCourseCategoryIds?: string[] | null;
      dateFrom?: string | null;
      dateTo?: string | null;
    }
  | undefined;

type FetchCourseCategoryStudentsArgsType = {
  courseId?: string | null;
  userId?: string | null;
  selectedCourseCategoryId?: string | null;
  studentIds?: UserRefType[] | null;
  includeInactive?: boolean;
};

type FetchCourseCategoryStudentGroupsArgsType = {
  courseId?: string | null;
  userId?: string | null;
  selectedCourseCategoryId?: string | null;
};

type CreateOneEventArgsType = {
  courseId?: string | null;
  userId?: string | null;
  selectedCourseCategoryId?: string | null;
  student?: string | null;
  studentsGroup?: string | null;
  lessonId?: string | null;
  title?: string | null;
  color?: string | null;
  dateTime?: string[] | null;
};

type UpdateByIdEventArgsType = {
  _id: string;
  courseId?: string | null;
  userId?: string | null;
  selectedCourseCategoryId?: string | null;
  student?: string | null;
  studentsGroup?: string | null;
  lessonId?: string | null;
  title?: string | null;
  color?: string | null;
  dateTime?: string[] | null;
};

type RemoveByIdEventArgsType = {
  _id: string;
};

type FindManyLightCourseCategoryQueryResultType = {
  findManyLightCourseCategoryQuery?: CourseCategoryType[];
};

type PaginationEventQueryResultType = {
  paginationCalendarLightEventQuery?: {
    items?: EventType[];
    pageInfo?: {
      currentPage?: number | null;
      perPage?: number | null;
      hasNextPage?: boolean | null;
    } | null;
  } | null;
};

type PaginationUserQueryResultType = {
  paginationUserQuery?: {
    items?: UserType[];
  } | null;
};

type PaginationUserGroupQueryResultType = {
  paginationUserGroupQuery?: {
    items?: UserGroupType[];
  } | null;
};

const initialState: CalendarStateType = {
  data: undefined,
  currIndex: 0,
  isLessonEventCreationMode: false,
  courseCategories: [],
  courseCategoryStudentGroups: [],
  courseCategoryStudents: [],
  courseId: null,
  selectedCourseCategory: null,
  selectedCourseCategoryId: null,
  selectedCourseCategoryIds: [],
  events: [],
  eventDetailsById: {},
  eventDetailsLoadingById: {},
  isLoading: false,
  error: null,
  contents: [],
};

type UserRefType =
  | string
  | {
      _id?: string | null;
      firstName?: string | null;
      lastName?: string | null;
      email?: string | null;
    }
  | null
  | undefined;

export const fetchEventDetailsById = createAsyncThunk<
  EventType | null,
  FetchEventDetailsByIdArgsType,
  { state: RootStateLike }
>('calendar/fetchEventDetailsById', async (args, { getState }) => {
  const cachedEvent = getState().calendar?.eventDetailsById?.[args._id];

  if (cachedEvent) {
    return cachedEvent;
  }

  const apolloClient = await initializeApollo();

  const result = await apolloClient.query<FindByIdEventQueryResultType>({
    query: findByIdEventQuery,
    variables: {
      _id: args._id,
    },
    fetchPolicy: 'cache-first',
  });

  return result.data.findByIdEventQuery ?? null;
});

const getUserId = (user: UserRefType): string | null => {
  if (!user) {
    return null;
  }

  if (typeof user === 'string') {
    return user;
  }

  return user._id ?? null;
};

export const fetchCourseCategories = createAsyncThunk<
  void,
  FetchCourseCategoriesArgsType | undefined,
  { state: RootStateLike }
>('calendar/fetchCourseCategories', async (args, { dispatch, getState }) => {
  const requestedCourseId = args?.courseId ?? null;

  if (!requestedCourseId) {
    dispatch(setCourseCategories([]));
    dispatch(setSelectedCourseCategoryIds([]));
    return;
  }

  const apolloClient = await initializeApollo();

  const result = await apolloClient.query<FindManyLightCourseCategoryQueryResultType>({
    query: findManyLightCourseCategoryQuery,
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        data: {
          course: requestedCourseId,
        },
      },
      limit: MAX_LIMIT_FOR_FETCHING_ITEMS,
    },
  });

  const currentCourseId = getState().calendar?.courseId ?? null;

  // Ignore a slower response for the course that was selected previously.
  if (currentCourseId && String(currentCourseId) !== String(requestedCourseId)) {
    return;
  }

  const categories: CourseCategoryType[] = (result.data.findManyLightCourseCategoryQuery ?? []).map((item) => ({
    ...item,
    data: {
      ...item.data,
      // The light client query may omit data.course. Keep the request course on
      // every returned module so calendar filters can never mix different courses.
      course: requestedCourseId,
    },
  }));

  dispatch(setCourseCategories(categories));
  dispatch(setSelectedCourseCategoryIds(categories.map((item) => item._id)));
});

export const fetchEvents = createAsyncThunk<void, FetchEventsArgsType, { state: RootStateLike }>(
  'calendar/fetchEvents',
  async (args, { dispatch, getState }) => {
    const state = getState();

    const isStudent = state.users?.userWithCourses?.role === USER_ROLE.STUDENT;

    const resolvedCourseId = args?.courseId ?? state.calendar?.courseId ?? null;
    const resolvedUserId = args?.userId ?? state.users?.userWithCourses?._id ?? null;

    const selectedCourseCategoryIds = args?.selectedCourseCategoryIds?.filter(Boolean) ?? [];

    const apolloClient = await initializeApollo();

    const allEvents: EventType[] = [];
    let page = 1;
    let hasNextPage = true;

    while (hasNextPage) {
      const result = await apolloClient.query<PaginationEventQueryResultType>({
        query: paginationCalendarLightEventQuery,
        fetchPolicy: 'network-only',
        variables: {
          filter: {
            course: resolvedCourseId,

            ...(selectedCourseCategoryIds.length > 0
              ? {
                  courseCategories: selectedCourseCategoryIds,
                }
              : {}),

            ...(args?.dateFrom && args?.dateTo
              ? {
                  dateFrom: args.dateFrom,
                  dateTo: args.dateTo,
                }
              : {}),

            ...(isStudent
              ? {
                  student: resolvedUserId,
                }
              : {
                  creator: resolvedUserId,
                }),
          },
          page,
          perPage: 500,
        },
      });

      const pagination = result.data.paginationCalendarLightEventQuery;
      const pageItems = pagination?.items ?? [];

      allEvents.push(...pageItems);

      hasNextPage = Boolean(pagination?.pageInfo?.hasNextPage);
      page += 1;
    }

    dispatch(setEvents(allEvents));
  },
);

export const fetchCourseCategoryStudents = createAsyncThunk<
  void,
  FetchCourseCategoryStudentsArgsType,
  { state: RootStateLike }
>('calendar/fetchCourseCategoryStudents', async (args, { dispatch }) => {
  const apolloClient = await initializeApollo();

  const studentIds = args.studentIds?.map(getUserId).filter((id): id is string => Boolean(id)) ?? [];

  const filter =
    studentIds.length > 0
      ? {
          _ids: studentIds,
          role: USER_ROLE.STUDENT,
          isRemoved: false,
          ...(args.includeInactive ? {} : { isActivated: true }),
        }
      : {
          course: args.courseId ?? null,
          teacher: args.userId ?? null,
          courseCategory: args.selectedCourseCategoryId ?? null,
          isActivated: true,
        };

  const result = await apolloClient.query<PaginationUserQueryResultType>({
    query: itemsPagination({
      entity: 'user',
      fragment: userFragment,
    }),
    variables: {
      filter,
      nextPage: 1,
      sort: '_ID_ASC',
      perPage: MAX_LIMIT_FOR_FETCHING_ITEMS,
    },
    fetchPolicy: 'network-only',
  });

  dispatch(setCourseCategoryStudents(result.data.paginationUserQuery?.items ?? []));
});

export const fetchCourseCategoryStudentGroups = createAsyncThunk<
  void,
  FetchCourseCategoryStudentGroupsArgsType,
  { state: RootStateLike }
>('calendar/fetchCourseCategoryStudentGroups', async (args, { dispatch }) => {
  const apolloClient = await initializeApollo();

  const result = await apolloClient.query<PaginationUserGroupQueryResultType>({
    query: itemsPagination({
      entity: 'userGroup',
      fragment: userGroupFragment,
      filterType: 'FilterFindManyUserGroupWithUsersInput',
    }),
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        data: {
          course: args.courseId ?? null,
          teacher: args.userId ?? null,
          courseCategory: args.selectedCourseCategoryId ?? null,
        },
      },
      nextPage: 1,
      sort: '_ID_ASC',
      perPage: MAX_LIMIT_FOR_FETCHING_ITEMS,
    },
  });

  dispatch(setCourseCategoryStudentGroups(result.data.paginationUserGroupQuery?.items ?? []));
});

export const updateEventDateTime = createAsyncThunk<void, UpdateEventDateTimeArgsType, { state: RootStateLike }>(
  'calendar/updateEventDateTime',
  async (args, { dispatch }) => {
    const apolloClient = await initializeApollo();

    const result = await apolloClient.mutate({
      mutation: updateEventDateTimeMutation,
      variables: {
        _id: args._id,
        dateTime: args.dateTime,
      },
    });

    const updatedEvent = result.data?.updateEventDateTimeMutation;

    if (updatedEvent) {
      dispatch(updateEvent(updatedEvent));
    }
  },
);

export const createOneEvent = createAsyncThunk<void, CreateOneEventArgsType, { state: RootStateLike }>(
  'calendar/createOneEvent',
  async (args, { dispatch }) => {
    const apolloClient = await initializeApollo();

    const result = await apolloClient.mutate({
      mutation: createOneEventMutation,
      variables: {
        status: args.status,
        course: args.courseId ?? null,
        courseCategory: args.selectedCourseCategoryId ?? null,
        student: args.student ?? null,
        studentsGroup: args.studentsGroup ?? null,
        lesson: args.lessonId ?? null,
        title: args.title ?? null,
        color: args.color ?? null,
        dateTime: args.dateTime ?? [],
      },
    });

    const createdEvent = result.data?.createOneEventMutation;

    if (createdEvent) {
      dispatch(addEvent(createdEvent));
    }
  },
);

export const updateByIdEvent = createAsyncThunk<void, UpdateByIdEventArgsType, { state: RootStateLike }>(
  'calendar/updateByIdEvent',
  async (args, { dispatch }) => {
    const apolloClient = await initializeApollo();

    const result = await apolloClient.mutate({
      mutation: updateByIdEventMutation,
      variables: {
        _id: args._id,
        record: {
          status: args.status,
          data: {
            course: args.courseId ?? null,
            studentsGroup: args.studentsGroup ?? null,
            courseCategory: args.selectedCourseCategoryId ?? null,
            lesson: args.lessonId ?? null,
            users: args.student ? [args.student] : [],
          },
          color: args.color ?? null,
          title: args.title ?? null,
          dateTime: args.dateTime ?? [],
        },
      },
    });

    const updatedEvent = result.data?.updateByIdEventMutation;

    if (updatedEvent) {
      dispatch(updateEvent(updatedEvent));
    }
  },
);

export const removeByIdEvent = createAsyncThunk<void, RemoveByIdEventArgsType, { state: RootStateLike }>(
  'calendar/removeByIdEventMutation',
  async (args, { dispatch }) => {
    const apolloClient = await initializeApollo();

    await apolloClient.mutate({
      mutation: removeByIdEventMutation,
      variables: {
        _id: args._id,
      },
    });

    dispatch(removeEvent(args._id));
  },
);

export const removeByIdsEvent = createAsyncThunk<void, RemoveByIdEventArgsType, { state: RootStateLike }>(
  'calendar/removeByIdEventMutation',
  async (args, { dispatch }) => {
    const apolloClient = await initializeApollo();

    await apolloClient.mutate({
      mutation: removeByIdsEventMutation,
      variables: {
        ids: args.ids,
      },
    });

    args.ids.forEach((id) => {
      dispatch(removeEvent(id));
    });
  },
);

export const calendarSlice = createSlice({
  name: 'calendar',
  initialState,
  reducers: {
    addEvent: (state, action: PayloadAction<EventType>) => {
      state.events.push(action.payload);
    },

    updateEvent: (state, action: PayloadAction<EventType>) => {
      state.events = state.events.map((item) => (item._id === action.payload._id ? action.payload : item));
    },

    removeEvent: (state, action: PayloadAction<string>) => {
      state.events = state.events.filter((item) => item._id !== action.payload);
    },
    setEventDetails: (state, action: PayloadAction<EventType>) => {
      state.eventDetailsById[action.payload._id] = action.payload;
    },

    clearEventDetails: (state) => {
      state.eventDetailsById = {};
      state.eventDetailsLoadingById = {};
    },
    setIsLessonEventCreationMode: (state, action: PayloadAction<boolean>) => {
      state.isLessonEventCreationMode = action.payload;
    },
    setEvents: (state, action: PayloadAction<EventType[]>) => {
      state.events = action.payload;
    },
    setSelectedCourseCategory: (state, action: PayloadAction<string | null>) => {
      state.selectedCourseCategory = action.payload;
      state.selectedCourseCategoryId = action.payload;
    },
    setSelectedCourseCategoryIds: (state, action: PayloadAction<string[]>) => {
      state.selectedCourseCategoryIds = action.payload;
    },
    setCourseCategoryStudents: (state, action: PayloadAction<UserType[]>) => {
      state.courseCategoryStudents = action.payload;
    },
    setCourseCategoryStudentGroups: (state, action: PayloadAction<UserGroupType[]>) => {
      state.courseCategoryStudentGroups = action.payload;
    },
    setCourseId: (state, action: PayloadAction<string | null>) => {
      state.courseId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCourseCategories.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(fetchCourseCategories.fulfilled, (state) => {
      state.isLoading = false;
    });

    builder.addCase(fetchCourseCategories.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message ?? 'Unknown error';
    });

    builder.addCase(fetchEvents.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(fetchEvents.fulfilled, (state) => {
      state.isLoading = false;
    });

    builder.addCase(fetchEvents.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message ?? 'Unknown error';
    });

    builder.addCase(fetchCourseCategoryStudents.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(fetchCourseCategoryStudents.fulfilled, (state) => {
      state.isLoading = false;
    });

    builder.addCase(fetchCourseCategoryStudents.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message ?? 'Unknown error';
    });

    builder.addCase(fetchCourseCategoryStudentGroups.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(fetchCourseCategoryStudentGroups.fulfilled, (state) => {
      state.isLoading = false;
    });

    builder.addCase(fetchCourseCategoryStudentGroups.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message ?? 'Unknown error';
    });

    builder.addCase(createOneEvent.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(createOneEvent.fulfilled, (state) => {
      state.isLoading = false;
    });

    builder.addCase(createOneEvent.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message ?? 'Unknown error';
    });

    builder.addCase(updateByIdEvent.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(updateByIdEvent.fulfilled, (state) => {
      state.isLoading = false;
    });

    builder.addCase(updateByIdEvent.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message ?? 'Unknown error';
    });

    builder.addCase(removeByIdEvent.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(removeByIdEvent.fulfilled, (state) => {
      state.isLoading = false;
    });

    builder.addCase(removeByIdEvent.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message ?? 'Unknown error';
    });
    builder.addCase(fetchEventDetailsById.pending, (state, action) => {
      state.eventDetailsLoadingById[action.meta.arg._id] = true;
    });

    builder.addCase(fetchEventDetailsById.fulfilled, (state, action) => {
      const eventId = action.meta.arg._id;

      state.eventDetailsLoadingById[eventId] = false;

      if (action.payload?._id) {
        state.eventDetailsById[action.payload._id] = action.payload;
      }
    });

    builder.addCase(fetchEventDetailsById.rejected, (state, action) => {
      state.eventDetailsLoadingById[action.meta.arg._id] = false;
    });
  },
});

export const {
  setEvents,
  setCourseCategoryStudents,
  setCourseCategoryStudentGroups,
  setCourseId,
  setSelectedCourseCategory,
  setSelectedCourseCategoryIds,
  setIsLessonEventCreationMode,
  addEvent,
  updateEvent,
  removeEvent,
  setEventDetails,
  clearEventDetails,
} = calendarSlice.actions;

export default calendarSlice.reducer;
